// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useSearch } from 'wouter';
import { useI18n } from '../i18n';
import ConfirmModal from '../components/ConfirmModal';
import { useAuth } from '../context/AuthContext';
import AnalysisCard from '../components/AnalysisCard';
import {
  getProjects, getProject, createProject, updateProject, deleteProject,
  addProjectDocumentFile, addProjectDocumentManual, deleteProjectDocument,
  validateProject, getProjectAnalyses, toggleProjectDocument,
} from '../services/api';
import {
  FolderOpen, FolderPlus, Trash2, Upload, FileText, FilePlus2,
  ChevronRight, AlertCircle, CheckCircle2, Clock, Edit3, Save, X,
  PlayCircle, PencilLine, ShieldCheck, BarChart2, EyeOff, Eye, Info,
} from 'lucide-react';

// ─── Constantes ──────────────────────────────────────────────────────────────

const MISSION_TYPES = ['RAAT', 'DAAT'];

// Acronyme AFFICHÉ, là où le libellé complet ne tient pas (liste des projets).
// ⚠️ La valeur stockée d'une mission de démolition est `DAAT`, son terme métier est
// **RAAD** — celui du message de refus (`utils/mission_filter.py`) et de `/aide`.
// La liste affichait la valeur brute et était donc le seul écran à dire encore DAAT.
// ⛔ Ne pas « corriger » MISSION_TYPES : des projets en base portent déjà `DAAT`,
// ce serait une migration, pas un renommage.
const MISSION_ACRONYME = { RAAT: 'RAAT', DAAT: 'RAAD' };

// Types de documents selon la mission
const DOC_TYPES_RAAT = ['programme_travaux', 'plan', 'ancien_rapport', 'cctp', 'autre'];
const DOC_TYPES_DAAT = ['plan', 'ancien_rapport', 'autre'];

const DOC_TYPE_KEYS = {
  programme_travaux: 'project_doc_programme',
  plan:              'project_doc_plan',
  ancien_rapport:    'project_doc_ancien',
  cctp:              'project_doc_cctp',
  saisie_manuelle:   'project_doc_manual',
  autre:             'project_doc_autre',
};

const STATUS_ICONS = {
  pending:      <Clock size={13} style={{ color: '#f59e0b' }} />,
  processing:   <Clock size={13} style={{ color: '#3b82f6' }} className="nms-spin-slow" />,
  done:         <CheckCircle2 size={13} style={{ color: '#10b981' }} />,
  error:        <AlertCircle size={13} style={{ color: '#ef4444' }} />,
  manual:       <CheckCircle2 size={13} style={{ color: '#6366f1' }} />,
  ocr_required: <AlertCircle size={13} style={{ color: '#f97316' }} />,
};

const STATUS_KEYS = {
  pending:      'project_doc_status_pending',
  processing:   'project_doc_status_processing',
  done:         'project_doc_status_done',
  error:        'project_doc_status_error',
  manual:       'project_doc_status_manual',
  ocr_required: 'project_doc_status_ocr',
};

const MAX_DOCS = 20;
const MAX_SIZE_MB = 250;

// Composants Annexe A1 NF X 46-020 — groupés par section
const COMPOSANTS_A1 = [
  // Section 1 — Toitures
  { id: 'toiture',          fr: 'Toiture / Couverture',                              en: 'Roof / Covering',                                section: 1 },
  { id: 'etancheite',       fr: 'Étanchéité toiture-terrasse',                       en: 'Flat roof waterproofing',                        section: 1 },
  // Section 2 — Façades extérieures
  { id: 'facades_ext',      fr: 'Façades extérieures (enduits, bardages, crépis)',   en: 'Exterior facades (render, cladding)',             section: 2 },
  { id: 'menuiseries_ext',  fr: 'Menuiseries extérieures (joints, vitrages)',        en: 'External joinery (seals, glazing)',               section: 2 },
  // Section 3 — Parois intérieures
  { id: 'cloisons',         fr: 'Cloisons intérieures (sèches et maçonnées)',        en: 'Interior partitions (dry and masonry)',           section: 3 },
  { id: 'enduits_flocages', fr: 'Enduits, flocages, peintures intérieures',          en: 'Interior renders, sprayed coatings, paints',      section: 3 },
  { id: 'poteaux',          fr: 'Poteaux / Structure béton',                         en: 'Columns / Concrete structure',                   section: 3 },
  { id: 'gaines_coffres',   fr: 'Gaines et coffres (verticaux et horizontaux)',      en: 'Vertical and horizontal ducts / chases',          section: 3 },
  { id: 'portes_cf',        fr: 'Portes coupe-feu, pare-flamme, isothermiques',      en: 'Fire doors, flame-retardant, isothermal',         section: 3 },
  { id: 'rev_murs',         fr: 'Revêtements de murs (carrelage, colles, ragréages)', en: 'Wall coverings (tiles, adhesives, screeds)',     section: 3 },
  // Section 4 — Plafonds
  { id: 'plafonds',         fr: 'Plafonds (flocages, enduits projetés)',              en: 'Ceilings (sprayed coatings, renders)',            section: 4 },
  { id: 'faux_plafonds',    fr: 'Faux-plafonds (panneaux, suspentes, joints)',        en: 'Suspended ceilings (panels, hangers, joints)',    section: 4 },
  { id: 'poutres',          fr: 'Poutres / Charpentes (flocages, peintures intum.)',  en: 'Beams / Structures (coatings, intumescent paint)', section: 4 },
  // Section 5 — Sols
  { id: 'rev_sol',          fr: 'Revêtements de sol (dalles, moquettes, colles)',    en: 'Floor coverings (tiles, carpets, adhesives)',     section: 5 },
  // Section 6 — Réseaux
  { id: 'conduits',         fr: 'Conduits / Réseaux (air, eau, vapeur, fumée)',      en: 'Ducts / Networks (air, water, steam, smoke)',     section: 6 },
  { id: 'clapets',          fr: 'Clapets et volets coupe-feu',                       en: 'Fire dampers and shutters',                      section: 6 },
  // Section 7 — Ascenseurs
  { id: 'ascenseurs',       fr: 'Ascenseurs, monte-charges',                         en: 'Lifts, freight elevators',                       section: 7 },
  // Section 8 — Équipements
  { id: 'equipements_therm',  fr: 'Équipements thermiques (chaudières, radiateurs)', en: 'Thermal equipment (boilers, radiators)',          section: 8 },
  { id: 'installations_elec', fr: 'Installations électriques (canalisations)',        en: 'Electrical installations (conduits, cables)',     section: 8 },
];

// ─── Helper ───────────────────────────────────────────────────────────────────

function isDAATMode(m) { return m === 'DAAT'; }

// ─── Composant principal ──────────────────────────────────────────────────────

const Projects = () => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [location, setLocation] = useLocation();

  // Même règle que `verifier_droit_a_consommer()` côté serveur : seuls les
  // comptes réellement gouvernés par un quota sont concernés. En cas de doute on
  // ne bloque PAS — un faux blocage est pire qu'un refus tardif.
  const quotaExhausted = (
    !['admin', 'dev'].includes(user?.role)
    && !user?.pricing_tier_id
    && !user?.license_id
    && user?.quota_remaining != null
    && user.quota_remaining <= 0
  );
  // Droits suspendus par le serveur (paiement en échec…) — même règle que le
  // dépôt du Dashboard : `quotaExhausted` ne voit pas un abonné en `past_due`.
  const droitsSuspendus = !!user?.access_warning?.bloquant;

  // ✅ FIX bug navigation : wouter v3 useSearch() retourne la query string
  // (sans le '?') et déclenche un re-render quand elle change. Auparavant
  // location.split('?')[1] était toujours undefined car wouter n'expose pas
  // la query dans useLocation → projets jamais sélectionnés depuis la sidebar.
  const searchString = useSearch();

  // Liste projets
  const [projects, setProjects]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedId, setSelectedId]     = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // Création projet
  const [showCreate, setShowCreate]     = useState(false);
  const [newName, setNewName]           = useState('');
  const [newDesc, setNewDesc]           = useState('');
  const [newMission, setNewMission]     = useState('');
  const [newAddress, setNewAddress]     = useState('');
  const [creating, setCreating]         = useState(false);

  // Edition projet
  const [editMode, setEditMode]         = useState(false);
  const [editName, setEditName]         = useState('');
  const [editDesc, setEditDesc]         = useState('');
  const [editMission, setEditMission]   = useState('');
  const [editAddress, setEditAddress]   = useState('');
  const [saving, setSaving]             = useState(false);

  // Onglets détail
  const [activeTab, setActiveTab]       = useState('docs'); // 'docs' | 'manual' | 'summary'

  // Upload fichier
  const [docType, setDocType]           = useState('programme_travaux');
  const [dragOver, setDragOver]         = useState(false);
  const [uploading, setUploading]       = useState(false);
  const fileInputRef                    = useRef(null);

  // Saisie manuelle — "Périmètre et programme des travaux" (4 sous-onglets)
  const [demoType, setDemoType]         = useState('totale'); // 'totale' | 'partielle' (DAAT only)
  const [batiments, setBatiments]       = useState([]); // [{id, nom, locaux:[{id,name,level}]}]
  const [descriptionProgramme, setDescriptionProgramme] = useState('');
  const [constraints, setConstraints]   = useState('');
  const [exclusions, setExclusions]     = useState([]);
  const [subTab, setSubTab]             = useState('locaux'); // 'locaux'|'programme'|'comments'|'exclusions'
  const [savingManual, setSavingManual] = useState(false);

  // Validation projet
  const [validating, setValidating]     = useState(false);

  // Onglet analyses
  const [analyses, setAnalyses]           = useState([]);
  const [loadingAnalyses, setLoadingAnalyses] = useState(false);

  // Confirm delete
  const [confirmProject, setConfirmProject] = useState(null);
  const [confirmDoc, setConfirmDoc]         = useState(null);

  const [error, setError]               = useState('');

  // Helpers mission + statut
  const missionType = selectedProject?.mission_type || '';
  const isDAAT = isDAATMode(missionType);
  const isActive = selectedProject?.status === 'active'; // projet validé (extraction faite)
  // Docs actifs en attente d'extraction (ajoutés depuis la dernière validation)
  const pendingDocs = (selectedProject?.documents || []).filter(
    d => d.doc_type !== 'saisie_manuelle'
      && d.extraction_status === 'pending'
      && d.is_active !== false && d.is_active !== 0
  );
  // Docs actifs en cours d'extraction (thread Mistral actif côté backend)
  const processingDocs = (selectedProject?.documents || []).filter(
    d => d.doc_type !== 'saisie_manuelle'
      && d.extraction_status === 'processing'
      && d.is_active !== false && d.is_active !== 0
  );
  const isProcessing = processingDocs.length > 0;

  const docTypes = isDAAT ? DOC_TYPES_DAAT : DOC_TYPES_RAAT;

  // Libellé composant selon langue
  const compLabel = (c) => lang === 'en' ? c.en : c.fr;

  // ── Chargement liste projets ──────────────────────────────────────────────
  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProjects();
      setProjects(data.projects || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // Auto-sélection depuis URL param ?id=<project_id>
  // ✅ FIX : on utilise searchString (wouter useSearch) au lieu de location.split,
  // car wouter useLocation retourne uniquement le pathname (sans query string).
  useEffect(() => {
    const params = new URLSearchParams(searchString || '');
    const idFromUrl = params.get('id');
    if (idFromUrl && idFromUrl !== selectedId) {
      selectProject(idFromUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString]);

  // Adapter docType par défaut selon mission
  useEffect(() => {
    if (isDAAT && docType === 'programme_travaux') setDocType('plan');
    if (isDAAT && docType === 'cctp') setDocType('plan');
  }, [isDAAT, docType]);

  // Polling auto : tant qu'au moins un doc est en extraction, refetch toutes les 5s
  //
  // ⚠️ La LISTE aussi, pas seulement le détail. Elle n'était rechargée qu'au
  // montage et après une action : pendant une extraction, le badge de la barre
  // latérale restait donc figé sur l'état d'avant validation — vert « Actif »
  // alors que le détail, lui, affichait bien « Extraction en cours ». Deux
  // sources de vérité pour le même projet, dont une périmée.
  useEffect(() => {
    if (!isProcessing || !selectedId) return;
    const interval = setInterval(() => {
      selectProject(selectedId);
      loadProjects();
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing, selectedId]);

  // ── Sélection projet ──────────────────────────────────────────────────────
  const selectProject = useCallback(async (id) => {
    setSelectedId(id);
    setEditMode(false);
    setActiveTab('docs');
    setBatiments([]);
    setDescriptionProgramme('');
    setConstraints('');
    setExclusions([]);
    setSubTab('locaux');
    setDemoType('totale');
    setAnalyses([]);
    try {
      const data = await getProject(id);
      setSelectedProject(data);
      setEditName(data.name || '');
      setEditDesc(data.description || '');
      setEditMission(data.mission_type || '');
      setEditAddress(data.building_address || '');
    } catch (e) {
      setError(e.message);
    }
  }, []);

  // ── Créer projet ──────────────────────────────────────────────────────────
  const [createErrors, setCreateErrors] = useState({});
  const handleCreate = async () => {
    const errors = {};
    if (!newName.trim()) errors.name = t('project_name_required');
    if (!newMission) errors.mission = t('project_mission_required');
    if (Object.keys(errors).length) { setCreateErrors(errors); return; }
    setCreateErrors({});
    setCreating(true);
    try {
      await createProject({
        name: newName.trim(),
        description: newDesc,
        mission_type: newMission,
        building_address: newAddress,
      });
      setNewName(''); setNewDesc(''); setNewMission(''); setNewAddress('');
      setShowCreate(false);
      await loadProjects();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  // ── Sauvegarder édition ───────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editMission) { setError(t('project_mission_required')); return; }
    setSaving(true);
    try {
      await updateProject(selectedId, {
        name: editName, description: editDesc,
        mission_type: editMission, building_address: editAddress,
      });
      await loadProjects();
      await selectProject(selectedId);
      setEditMode(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Supprimer projet ──────────────────────────────────────────────────────
  const handleDeleteProject = async () => {
    try {
      await deleteProject(confirmProject);
      if (selectedId === confirmProject) { setSelectedId(null); setSelectedProject(null); }
      await loadProjects();
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirmProject(null);
    }
  };

  // ── Upload fichier ────────────────────────────────────────────────────────
  const handleFileDrop = async (file) => {
    if (!file || !selectedId) return;

    // Quota épuisé : un document de projet est extrait PUIS analysé par le LLM.
    // Jusqu'au 27/07/2026 rien ne le bloquait — un compte au quota épuisé
    // produisait donc de la consommation réelle, facturée à Evalens, invisible
    // de tous les compteurs. Le serveur refuse désormais (403) ; on prévient ici
    // pour ne pas faire choisir un fichier avant de le refuser.
    if (droitsSuspendus) {
      setError(t('access_blocked_project_doc'));
      return;
    }
    if (quotaExhausted) {
      setError(t('quota_exhausted_project_doc'));
      return;
    }

    // Vérification limites côté client
    const uploadedDocs = (selectedProject?.documents || []).filter(d => d.doc_type !== 'saisie_manuelle');
    const effectiveCount = Math.max(selectedProject?.validated_doc_count || 0, uploadedDocs.length);
    if (effectiveCount >= MAX_DOCS) {
      setError(t('project_doc_limit_reached'));
      return;
    }
    const currentSizeMB = uploadedDocs.reduce((s, d) => s + (d.file_size || 0), 0) / (1024 * 1024);
    if (currentSizeMB + file.size / (1024 * 1024) > MAX_SIZE_MB) {
      setError(t('project_doc_size_limit'));
      return;
    }
    setUploading(true);
    try {
      await addProjectDocumentFile(selectedId, file, docType);
      await selectProject(selectedId);
      await loadProjects();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDropZone = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileDrop(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) handleFileDrop(file);
    e.target.value = '';
  };

  // ── Activer / désactiver document ────────────────────────────────────────
  const handleToggleDoc = async (docId, currentIsActive) => {
    try {
      await toggleProjectDocument(selectedId, docId, !currentIsActive);
      await selectProject(selectedId);
    } catch (e) {
      setError(e.message);
    }
  };

  // ── Supprimer document ────────────────────────────────────────────────────
  const handleDeleteDoc = async () => {
    if (!confirmDoc) return;
    try {
      await deleteProjectDocument(confirmDoc.projectId, confirmDoc.docId);
      await selectProject(selectedId);
      await loadProjects();
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirmDoc(null);
    }
  };

  // ── Saisie manuelle — bâtiments / locaux (flux ordonné) ──────────────────
  const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

  const addBatiment = () =>
    setBatiments(prev => [...prev, { id: uid(), nom: '', locaux: [] }]);
  const removeBatiment = (batId) => setBatiments(prev => prev.filter(b => b.id !== batId));
  const updateBatimentNom = (batId, nom) =>
    setBatiments(prev => prev.map(b => b.id === batId ? { ...b, nom } : b));
  const addLocal = (batId) => setBatiments(prev => prev.map(b =>
    b.id === batId ? { ...b, locaux: [...b.locaux, { id: uid(), name: '', level: '' }] } : b));
  const updateLocal = (batId, locId, field, val) => setBatiments(prev => prev.map(b =>
    b.id === batId ? { ...b, locaux: b.locaux.map(l => l.id === locId ? { ...l, [field]: val } : l) } : b));
  const removeLocal = (batId, locId) => setBatiments(prev => prev.map(b =>
    b.id === batId ? { ...b, locaux: b.locaux.filter(l => l.id !== locId) } : b));

  const allLocauxNames = useMemo(
    () => batiments.flatMap(b => b.locaux.map(l => l.name).filter(Boolean)),
    [batiments]
  );

  // ── Saisie manuelle — exclusions ──────────────────────────────────────────
  const addExclusion = () => setExclusions(prev => [...prev, {
    id: uid(), type: 'totale', tous_locaux: false, locaux: [],
    sous_type: null, commentaire_perimetre: '', commentaire_programme: '',
  }]);
  const updateExclusion = (id, patch) =>
    setExclusions(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  const removeExclusion = (id) => setExclusions(prev => prev.filter(e => e.id !== id));

  const handleSaveManual = async () => {
    if (!selectedId) return;
    setSavingManual(true);
    try {
      const zones = batiments.flatMap(b => b.locaux.map(l => l.name).filter(Boolean));
      const batimentsPayload = batiments
        .filter(b => b.nom.trim())
        .map(b => ({
          id: b.id, nom: b.nom,
          locaux: b.locaux.filter(l => l.name).map(l => ({ id: l.id, name: l.name, level: l.level })),
        }));
      const descProgTrim = descriptionProgramme.trim();

      const structuredData = {
        type: isDAAT ? 'demolition' : 'programme_travaux',
        source: 'saisie_manuelle',
        version: 2,
        ...(isDAAT ? { demo_type: demoType } : {}),
        batiments: batimentsPayload,
        batiments_couverts: batimentsPayload.map(b => b.nom),
        zones,
        perimetre: zones.length ? 'locaux' : 'batiment',
        description_programme: descProgTrim,
        composants: [], actions: [], zones_llm: [],
        programme_llm_status: descProgTrim ? 'processing' : null,
        descriptions_complementaires: constraints,
        exclusions,
        texte_brut: [descProgTrim, constraints].filter(Boolean).join('\n\n'),
      };

      await addProjectDocumentManual(selectedId, structuredData);
      setDemoType('totale');
      setBatiments([]);
      setDescriptionProgramme('');
      setConstraints('');
      setExclusions([]);
      setSubTab('locaux');
      await selectProject(selectedId);
      await loadProjects();
      setActiveTab('summary');
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingManual(false);
    }
  };

  // ── Lancer une analyse depuis ce projet ──────────────────────────────────
  const handleLaunchAnalysis = () => {
    sessionStorage.setItem('normascan_preselect_project', JSON.stringify({
      id: selectedId,
      name: selectedProject?.name || '',
      status: selectedProject?.status || 'active',
    }));
    setLocation('/dashboard');
  };

  // ── Valider / mettre à jour le projet ────────────────────────────────────
  const handleValidateProject = async () => {
    const confirmMsg = isActive ? t('project_revalidate_confirm') : t('project_validate_confirm');
    if (!window.confirm(confirmMsg)) return;
    setValidating(true);
    try {
      await validateProject(selectedId);
      await loadProjects();
      await selectProject(selectedId);
    } catch (e) {
      setError(e.message);
    } finally {
      setValidating(false);
    }
  };

  // ── Charger les analyses liées ────────────────────────────────────────────
  const loadAnalyses = useCallback(async (id) => {
    if (!id) return;
    setLoadingAnalyses(true);
    try {
      const data = await getProjectAnalyses(id);
      setAnalyses(data.analyses || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingAnalyses(false);
    }
  }, []);

  // ── Données résumé saisie ─────────────────────────────────────────────────
  const manualDocs = (selectedProject?.documents || []).filter(d => d.doc_type === 'saisie_manuelle');
  const latestManual = manualDocs[0]; // tri DESC côté backend

  // Structuration LLM de la description libre du programme en cours (onglet
  // "Description du programme de travaux") — flag séparé de isProcessing
  // (qui exclut volontairement saisie_manuelle pour ne pas polluer les
  // bannières documents uploadés).
  const isManualStructuring = latestManual?.extraction_status === 'processing';

  useEffect(() => {
    if (!isManualStructuring || !selectedId) return;
    const interval = setInterval(() => {
      selectProject(selectedId);
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualStructuring, selectedId]);

  // ─────────────────────────────────────────────────────────────────────────
  // Rendu
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', fontFamily: 'inherit' }}>

      {/* ── Panneau gauche : liste ─────────────────────────────────────── */}
      <div style={{
        width: 280, minWidth: 280, borderRight: '1px solid var(--gray)',
        display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)',
      }}>
        {/* Header liste */}
        <div style={{ padding: '18px 16px 12px', borderBottom: '1px solid var(--gray)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--black)' }}>
              {t('projects_title')}
            </span>
            <button
              onClick={() => { setShowCreate(v => !v); setError(''); }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 7, background: 'var(--blue)', color: 'white', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              <FolderPlus size={13} />
              {!showCreate ? t('projects_new') : '✕'}
            </button>
          </div>

          {/* Formulaire création */}
          {showCreate && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                value={newName}
                onChange={e => { setNewName(e.target.value); setCreateErrors(prev => ({ ...prev, name: undefined })); }}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                placeholder={t('project_name_placeholder')}
                style={{ ...inputStyle, ...(createErrors.name ? { borderColor: '#ef4444' } : {}) }}
                autoFocus
              />
              {createErrors.name && <span style={{ color: '#ef4444', fontSize: 11 }}>{createErrors.name}</span>}
              <input
                value={newAddress}
                onChange={e => setNewAddress(e.target.value)}
                placeholder={t('project_address_label')}
                style={inputStyle}
              />
              <select
                value={newMission}
                onChange={e => { setNewMission(e.target.value); setCreateErrors(prev => ({ ...prev, mission: undefined })); }}
                style={{ ...inputStyle, ...(createErrors.mission ? { borderColor: '#ef4444' } : {}) }}
              >
                <option value="">{t('project_mission_label')}…</option>
                {MISSION_TYPES.map(m => (
                  <option key={m} value={m}>{t(`project_mission_${m.toLowerCase()}`)}</option>
                ))}
              </select>
              {createErrors.mission && <span style={{ color: '#ef4444', fontSize: 11 }}>{createErrors.mission}</span>}
              <button
                onClick={handleCreate}
                disabled={creating}
                style={{ ...btnPrimary, opacity: creating ? 0.6 : 1 }}
              >
                {creating ? '⏳' : t('project_create')}
              </button>
            </div>
          )}
        </div>

        {/* Liste projets */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {loading && (
            <div style={{ padding: '20px 12px', color: 'var(--text-muted)', fontSize: 13 }}>⏳</div>
          )}
          {!loading && projects.length === 0 && (
            <div style={{ padding: '20px 12px', color: 'var(--text-muted)', fontSize: 13 }}>
              {t('projects_empty')}
            </div>
          )}
          {projects.map(p => (
            <div
              key={p.id}
              onClick={() => selectProject(p.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                background: selectedId === p.id ? 'rgba(59,130,246,0.1)' : 'transparent',
                color: selectedId === p.id ? 'var(--blue)' : 'var(--black)',
                fontWeight: selectedId === p.id ? 600 : 400,
                transition: 'background 0.15s', marginBottom: 2,
              }}
              onMouseEnter={e => { if (selectedId !== p.id) e.currentTarget.style.background = 'rgba(128,128,128,0.08)'; }}
              onMouseLeave={e => { if (selectedId !== p.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <FolderOpen size={15} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {p.doc_count} {t('project_doc_count')}
                    {p.mission_type ? ` · ${MISSION_ACRONYME[p.mission_type] || p.mission_type}` : ''}
                  </span>
                  {/* Même règle que le détail : `extraction_in_progress` vient
                      du serveur. Sans lui, la liste annonçait « Actif » sur un
                      projet dont les documents étaient encore en extraction.

                      ⚠️ Pour le projet OUVERT, on retient aussi `isProcessing`,
                      calculé sur ses documents déjà chargés. Les deux vues d'un
                      même projet ne peuvent alors plus se contredire — c'est le
                      défaut que Marc a constaté deux fois : détail bleu
                      « Extraction en cours », barre latérale verte « Actif ».
                      La liste peut dater d'un cycle de polling ; le détail, lui,
                      est là. */}
                  {(() => {
                    const enExtraction = Boolean(p.extraction_in_progress)
                      || (p.id === selectedId && isProcessing);
                    const actif = p.status === 'active' && !enExtraction;
                    const fond = enExtraction ? 'rgba(59,130,246,0.12)' : actif ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)';
                    const texte = enExtraction ? '#1e40af' : actif ? '#059669' : '#b45309';
                    const bord = enExtraction ? 'rgba(59,130,246,0.3)' : actif ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)';
                    return (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99,
                        background: fond, color: texte, border: `1px solid ${bord}`,
                      }}>
                        {enExtraction ? t('project_status_processing')
                          : actif ? t('project_status_active') : t('project_status_draft')}
                      </span>
                    );
                  })()}
                </div>
              </div>
              {selectedId === p.id && <ChevronRight size={13} style={{ flexShrink: 0 }} />}
            </div>
          ))}
        </div>
      </div>

      {/* ── Panneau droit : détail ─────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto', padding: 28, background: '#fff' }}>
        {!selectedProject ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', gap: 12 }}>
            <FolderOpen size={48} style={{ opacity: 0.3 }} />
            <span style={{ fontSize: 14 }}>{t('projects_empty')}</span>
          </div>
        ) : (
          <>
            {/* En-tête projet */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {editMode ? (
                  <input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    style={{ ...inputStyle, fontSize: 20, fontWeight: 700, marginBottom: 0 }}
                    autoFocus
                  />
                ) : (
                  <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--black)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedProject.name}
                  </h1>
                )}
                {!editMode && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                    {selectedProject.mission_type && (
                      <span style={{ fontSize: 12, background: isDAAT ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)', color: isDAAT ? '#dc2626' : 'var(--blue)', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>
                        {t(`project_mission_${selectedProject.mission_type.toLowerCase()}`)}
                      </span>
                    )}
                    {/* ⚠️ Trois états, pas deux. Le projet passe en `active` dès
                        la validation, sans attendre l'extraction : le badge
                        s'affichait vert « Actif » pendant que le bandeau
                        au-dessous disait « veuillez patienter ». */}
                    {(() => {
                      const badge = isProcessing
                        ? { fond: 'rgba(59,130,246,0.1)',  texte: '#1e40af', bord: 'rgba(59,130,246,0.3)',  icone: <Clock size={11} className="nms-spin-slow" />, libelle: t('project_status_processing') }
                        : isActive
                          ? { fond: 'rgba(16,185,129,0.1)', texte: '#059669', bord: 'rgba(16,185,129,0.3)', icone: <ShieldCheck size={11} />, libelle: t('project_status_active') }
                          : { fond: 'rgba(245,158,11,0.1)', texte: '#b45309', bord: 'rgba(245,158,11,0.3)', icone: <Clock size={11} />, libelle: t('project_status_draft') };
                      return (
                        <span style={{
                          fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                          background: badge.fond,
                          color: badge.texte,
                          border: `1px solid ${badge.bord}`,
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          {badge.icone}
                          {badge.libelle}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, marginLeft: 16, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {editMode ? (
                  <>
                    <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, gap: 5, display: 'flex', alignItems: 'center' }}>
                      <Save size={13} />{saving ? '⏳' : t('project_update')}
                    </button>
                    <button onClick={() => setEditMode(false)} style={{ ...btnSecondary, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <X size={13} />{t('pref_cancel')}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Lancer une analyse — projet actif ET documents extraits.
                        Pendant l'extraction, le contexte projet n'est encore lu
                        nulle part (seuls les documents `done` le rejoignent) :
                        l'analyse serait facturée en comparant le rapport à un
                        projet incomplet. Le serveur refuse aussi, ce bouton
                        évite d'y arriver. */}
                    {isActive && (
                      <button
                        onClick={handleLaunchAnalysis}
                        disabled={isProcessing}
                        title={isProcessing ? t('project_launch_blocked') : t('project_launch_analysis')}
                        style={{ ...btnSuccess, display: 'flex', alignItems: 'center', gap: 6,
                                 opacity: isProcessing ? 0.5 : 1,
                                 cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                      >
                        <PlayCircle size={14} />{t('project_launch_analysis')}
                      </button>
                    )}
                    {/* Valider (draft) ou Mettre à jour (active + docs en attente) */}
                    {(!isActive || pendingDocs.length > 0 || isProcessing) && (
                      <button
                        onClick={handleValidateProject}
                        disabled={validating || isProcessing}
                        title={isProcessing ? t('project_processing_docs') : undefined}
                        style={{ ...btnValidate, display: 'flex', alignItems: 'center', gap: 6, opacity: (validating || isProcessing) ? 0.5 : 1, cursor: (validating || isProcessing) ? 'not-allowed' : 'pointer' }}
                      >
                        <ShieldCheck size={14} />
                        {validating || isProcessing ? '⏳' : isActive ? t('project_revalidate') : t('project_validate')}
                      </button>
                    )}
                    <button onClick={() => setEditMode(true)} style={{ ...btnSecondary, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <PencilLine size={13} />{t('project_edit_info')}
                    </button>
                    <button onClick={() => setConfirmProject(selectedId)} style={{ ...btnDanger, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Trash2 size={13} />{t('delete')}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Champs édition */}
            {editMode && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
                <div>
                  <label style={labelStyle}>{t('project_address_label')}</label>
                  <input value={editAddress} onChange={e => setEditAddress(e.target.value)} style={inputStyle} placeholder="…" />
                </div>
                <div>
                  <label style={labelStyle}>{t('project_mission_label')}</label>
                  <select value={editMission} onChange={e => setEditMission(e.target.value)} style={inputStyle}>
                    {MISSION_TYPES.map(m => (
                      <option key={m} value={m}>{t(`project_mission_${m.toLowerCase()}`)}</option>
                    ))}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>{t('project_desc_label')}</label>
                  <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} style={{ ...inputStyle, height: 64, resize: 'vertical' }} />
                </div>
              </div>
            )}

            {/* Infos lecture */}
            {!editMode && (selectedProject.building_address || selectedProject.description) && (
              <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {selectedProject.building_address && (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>📍 {selectedProject.building_address}</span>
                )}
                {selectedProject.description && (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selectedProject.description}</span>
                )}
              </div>
            )}

            {/* Séparateur */}
            <div style={{ borderTop: '1px solid var(--gray)', marginBottom: 20 }} />

            {/* Alerte docs en cours d'extraction (priorité 1) */}
            {isActive && isProcessing && (
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.25)', fontSize: 13, color: '#1e40af' }}>
                <Clock size={13} style={{ flexShrink: 0 }} className="nms-spin-slow" />
                <span>
                  <strong>{processingDocs.length}</strong> {t('project_processing_docs')}
                </span>
              </div>
            )}

            {/* Alerte docs en attente d'extraction (priorité 2 — si pas de processing en cours) */}
            {isActive && !isProcessing && pendingDocs.length > 0 && (
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', fontSize: 13, color: '#92400e' }}>
                <Clock size={13} style={{ flexShrink: 0 }} />
                <span>
                  <strong>{pendingDocs.length}</strong> {t('project_pending_docs')} — {t('project_revalidate')} {t('project_pending_docs_action')}
                </span>
              </div>
            )}

            {/* Onglets */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--gray)' }}>
              {[
                { key: 'docs',     label: t('project_docs_tab') },
                { key: 'manual',   label: t('project_manual_tab') },
                { key: 'summary',  label: t('project_summary_tab') },
                { key: 'analyses', label: t('project_analyses_tab') },
              ].map(tab => {
                // Une saisie manuelle existe déjà et on n'est pas en train de
                // l'éditer (arrivé via "Modifier", qui pré-remplit le formulaire)
                // → bloquer l'accès direct par l'onglet pour empêcher un
                // enregistrement vierge d'écraser silencieusement la saisie
                // existante. Seul "Modifier" depuis Résumé saisie doit permettre
                // l'édition (setActiveTab('manual') y est appelé après pré-remplissage).
                const locked = tab.key === 'manual' && manualDocs.length > 0 && activeTab !== 'manual';
                return (
                  <button
                    key={tab.key}
                    disabled={locked}
                    title={locked ? t('project_manual_tab_locked_hint') : undefined}
                    onClick={() => { if (locked) return; setActiveTab(tab.key); if (tab.key === 'analyses') loadAnalyses(selectedId); }}
                    style={{
                      padding: '8px 18px', border: 'none', background: 'transparent',
                      cursor: locked ? 'not-allowed' : 'pointer',
                      fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 400,
                      color: locked ? 'var(--text-muted)' : (activeTab === tab.key ? 'var(--blue)' : 'var(--text-muted)'),
                      opacity: locked ? 0.5 : 1,
                      borderBottom: activeTab === tab.key ? '2px solid var(--blue)' : '2px solid transparent',
                      marginBottom: -1, transition: 'color 0.15s',
                    }}
                  >
                    {tab.label}
                    {tab.key === 'summary' && manualDocs.length > 0 && (
                      <span style={{ marginLeft: 5, fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 99, background: '#6366f1', color: 'white' }}>✓</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* ── Onglet Documents ─────────────────────────────────────────── */}
            {activeTab === 'docs' && (
              <div>
                {/* ── Compteur + info limites ─────────────────────────────────── */}
                {(() => {
                  const uploadedDocs = (selectedProject.documents || []).filter(d => d.doc_type !== 'saisie_manuelle');
                  const validatedCount = selectedProject.validated_doc_count || 0;
                  const effectiveCount = Math.max(validatedCount, uploadedDocs.length);
                  const docsValidated = uploadedDocs.filter(d => d.extraction_status === 'done' || d.extraction_status === 'error').length;
                  const totalSizeMB = uploadedDocs.reduce((s, d) => s + (d.file_size || 0), 0) / (1024 * 1024);
                  const atLimit = effectiveCount >= MAX_DOCS;
                  const barColor = atLimit ? '#ef4444' : effectiveCount >= 15 ? '#f59e0b' : '#6366f1';

                  return (
                    <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: 'var(--bg-surface)', border: '1px solid var(--gray)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        {/* Compteur X/20 */}
                        <span title={t('project_doc_counter_tooltip')} style={{ fontSize: 15, fontWeight: 700, color: barColor, cursor: 'help' }}>
                          {effectiveCount}/{MAX_DOCS}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>·</span>
                        {/* Docs validés */}
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--black)' }}>
                            {docsValidated} {t('project_doc_validated_label')}
                          </span>
                          <span title={t('project_doc_validated_tooltip')} style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                            <Info size={13} style={{ color: 'var(--text-muted)' }} />
                          </span>
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>·</span>
                        {/* Taille */}
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {totalSizeMB.toFixed(1)} / {MAX_SIZE_MB} MB
                        </span>
                      </div>
                      {/* Barre de progression */}
                      <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.06)' }}>
                        <div style={{ height: '100%', borderRadius: 2, background: barColor, width: `${Math.min(100, (effectiveCount / MAX_DOCS) * 100)}%`, transition: 'width 0.3s' }} />
                      </div>
                      {/* Avertissement limite */}
                      {atLimit && (
                        <div style={{ marginTop: 6, fontSize: 11, color: '#ef4444', fontWeight: 500 }}>
                          ⚠️ {t('project_doc_limit_reached')}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Zone upload ─────────────────────────────────────────────── */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
                    <select value={docType} onChange={e => setDocType(e.target.value)} style={{ ...inputStyle, flex: 'none', width: 220 }}>
                      {docTypes.map(dt => (
                        <option key={dt} value={dt}>{t(DOC_TYPE_KEYS[dt] || dt)}</option>
                      ))}
                    </select>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('project_upload_types')}</span>
                  </div>

                  {(() => {
                    const uploadedDocs = (selectedProject.documents || []).filter(d => d.doc_type !== 'saisie_manuelle');
                    const effectiveCount = Math.max(selectedProject.validated_doc_count || 0, uploadedDocs.length);
                    const atLimit = effectiveCount >= MAX_DOCS;
                    return atLimit ? (
                      <div style={{
                        border: '2px dashed rgba(239,68,68,0.3)', borderRadius: 10, padding: '28px 20px',
                        textAlign: 'center', background: 'rgba(239,68,68,0.03)', opacity: 0.7,
                      }}>
                        <AlertCircle size={22} style={{ color: '#ef4444', marginBottom: 8 }} />
                        <div style={{ fontSize: 13, color: '#ef4444' }}>{t('project_doc_limit_reached')}</div>
                      </div>
                    ) : (
                      <div
                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleDropZone}
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          border: `2px dashed ${dragOver ? 'var(--blue)' : 'var(--gray)'}`,
                          borderRadius: 10, padding: '28px 20px',
                          textAlign: 'center', cursor: 'pointer',
                          background: dragOver ? 'rgba(59,130,246,0.05)' : 'var(--bg-surface)',
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                      >
                        {uploading
                          ? <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>⏳ Upload en cours…</span>
                          : <>
                              <Upload size={22} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
                              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('project_upload_drop')}</div>
                            </>
                        }
                      </div>
                    );
                  })()}
                  <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileInput} />
                </div>

                {/* ── Info limite ─────────────────────────────────────────────── */}
                <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 6, background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.12)', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  <Info size={12} style={{ verticalAlign: -2, marginRight: 4, color: '#6366f1' }} />
                  {t('project_doc_limit_info')}
                </div>

                {/* ── Liste documents (hors saisies manuelles) ───────────────── */}
                {(() => {
                  const uploadedDocs = (selectedProject.documents || []).filter(d => d.doc_type !== 'saisie_manuelle');
                  if (uploadedDocs.length === 0) {
                    return <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>{t('no_data')}</div>;
                  }
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {uploadedDocs.map(doc => {
                        const docActive = doc.is_active !== false && doc.is_active !== 0;
                        const isOcrRequired = doc.extraction_status === 'ocr_required';
                        const isDocProcessing = doc.extraction_status === 'processing';
                        return (
                          <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, border: `1px solid ${isOcrRequired ? 'rgba(249,115,22,0.3)' : docActive ? 'var(--gray)' : 'rgba(239,68,68,0.25)'}`, background: isOcrRequired ? 'rgba(249,115,22,0.04)' : docActive ? 'var(--bg-surface)' : 'rgba(239,68,68,0.03)', opacity: docActive && !isOcrRequired ? 1 : 0.65 }}>
                            <FileText size={16} style={{ color: isOcrRequired ? '#f97316' : docActive ? 'var(--text-muted)' : '#ef4444', flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--black)' }}>
                                {doc.filename || t('project_doc_manual')}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                  {t(DOC_TYPE_KEYS[doc.doc_type] || doc.doc_type)}
                                </span>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>·</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: isOcrRequired ? '#f97316' : 'var(--text-muted)' }}>
                                  {STATUS_ICONS[doc.extraction_status] || STATUS_ICONS.pending}
                                  {t(STATUS_KEYS[doc.extraction_status] || 'project_doc_status_pending')}
                                </span>
                                {!docActive && !isOcrRequired && (
                                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>
                                    Désactivé
                                  </span>
                                )}
                                {doc.file_size > 0 && (
                                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                    ({(doc.file_size / (1024 * 1024)).toFixed(1)} MB)
                                  </span>
                                )}
                              </div>
                              {/* Avertissement OCR */}
                              {isOcrRequired && (
                                <div style={{ marginTop: 4, fontSize: 11, color: '#f97316', lineHeight: 1.4, padding: '4px 8px', borderRadius: 4, background: 'rgba(249,115,22,0.06)' }}>
                                  ⚠️ {t('project_doc_ocr_warning')}
                                </div>
                              )}
                            </div>
                            {/* Toggle actif/inactif — désactivé pendant l'extraction */}
                            <button
                              onClick={() => !isDocProcessing && handleToggleDoc(doc.id, docActive)}
                              disabled={isDocProcessing}
                              title={isDocProcessing ? t('project_doc_status_processing') : (docActive ? 'Exclure de l\'analyse' : 'Inclure dans l\'analyse')}
                              style={{ background: 'none', border: 'none', cursor: isDocProcessing ? 'not-allowed' : 'pointer', color: docActive ? 'var(--text-muted)' : '#ef4444', padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center', opacity: isDocProcessing ? 0.35 : 1 }}
                              onMouseEnter={e => { if (!isDocProcessing) e.currentTarget.style.color = docActive ? '#f59e0b' : '#059669'; }}
                              onMouseLeave={e => { if (!isDocProcessing) e.currentTarget.style.color = docActive ? 'var(--text-muted)' : '#ef4444'; }}
                            >
                              {docActive ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                            {/* Suppression — désactivée pendant l'extraction (risque de plantage du thread) */}
                            <button
                              onClick={() => !isDocProcessing && setConfirmDoc({ projectId: selectedId, docId: doc.id })}
                              disabled={isDocProcessing}
                              title={isDocProcessing ? t('project_doc_status_processing') : 'Supprimer définitivement'}
                              style={{ background: 'none', border: 'none', cursor: isDocProcessing ? 'not-allowed' : 'pointer', color: 'var(--text-muted)', padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center', opacity: isDocProcessing ? 0.35 : 1 }}
                              onMouseEnter={e => { if (!isDocProcessing) e.currentTarget.style.color = '#ef4444'; }}
                              onMouseLeave={e => { if (!isDocProcessing) e.currentTarget.style.color = 'var(--text-muted)'; }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Onglet Saisie manuelle — "Périmètre et programme des travaux" ── */}
            {activeTab === 'manual' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 640 }}>

                {isDAAT && (
                  <div>
                    <label style={labelStyle}>{t('project_manual_demo_type')}</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {['totale', 'partielle'].map(dt => (
                        <button
                          key={dt}
                          onClick={() => setDemoType(dt)}
                          style={{
                            padding: '8px 20px', borderRadius: 8, border: '1.5px solid',
                            borderColor: demoType === dt ? (dt === 'totale' ? '#dc2626' : '#ea580c') : 'var(--gray)',
                            background: demoType === dt ? (dt === 'totale' ? 'rgba(220,38,38,0.08)' : 'rgba(234,88,12,0.08)') : 'transparent',
                            color: demoType === dt ? (dt === 'totale' ? '#dc2626' : '#ea580c') : 'var(--black)',
                            fontWeight: demoType === dt ? 700 : 400,
                            fontSize: 13, cursor: 'pointer',
                          }}
                        >
                          {t(`project_manual_demo_${dt}`)}
                        </button>
                      ))}
                    </div>
                    {demoType === 'totale' && (
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                        {t('project_manual_demo_totale_hint')}
                      </p>
                    )}
                  </div>
                )}

                {(!isDAAT || demoType === 'partielle') && (
                  <div>
                    <label style={labelStyle}>{t('project_manual_zone_title')}</label>

                    {/* Sous-onglets */}
                    <div style={{ display: 'flex', gap: 0, marginBottom: 14, borderBottom: '1px solid var(--gray)' }}>
                      {[
                        { key: 'locaux',     label: t('project_manual_subtab_locaux') },
                        { key: 'programme',  label: t('project_manual_subtab_programme') },
                        { key: 'comments',   label: t('project_manual_subtab_comments') },
                        { key: 'exclusions', label: t('project_manual_subtab_exclusions') },
                      ].map(st => (
                        <button
                          key={st.key}
                          onClick={() => setSubTab(st.key)}
                          style={{
                            padding: '6px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
                            fontSize: 12, fontWeight: subTab === st.key ? 700 : 400,
                            color: subTab === st.key ? 'var(--blue)' : 'var(--text-muted)',
                            borderBottom: subTab === st.key ? '2px solid var(--blue)' : '2px solid transparent',
                            marginBottom: -1,
                          }}
                        >
                          {st.label}
                        </button>
                      ))}
                    </div>

                    {/* Sous-onglet : Liste des locaux (flux bâtiment → locaux) */}
                    {subTab === 'locaux' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {batiments.map(bat => (
                          <div key={bat.id} style={{ border: '1px solid var(--gray)', borderRadius: 10, padding: 14 }}>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                              <input
                                value={bat.nom}
                                onChange={e => updateBatimentNom(bat.id, e.target.value)}
                                placeholder={t('project_manual_bat_placeholder')}
                                style={{ ...inputStyle, flex: 1, fontWeight: 600 }}
                              />
                              <button onClick={() => removeBatiment(bat.id)} title={t('project_manual_remove_bat')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4, display: 'flex', alignItems: 'center' }}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                            {!bat.nom.trim() ? (
                              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>{t('project_manual_local_missing_bat')}</p>
                            ) : bat.locaux.length === 0 ? (
                              <button
                                onClick={() => addLocal(bat.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--blue)', fontWeight: 500 }}
                              >
                                <FilePlus2 size={14} />{t('project_manual_add_locaux_for_bat')}
                              </button>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {bat.locaux.map(local => (
                                  <div key={local.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <input
                                      value={local.name}
                                      onChange={e => updateLocal(bat.id, local.id, 'name', e.target.value)}
                                      placeholder={t('project_manual_local_name')}
                                      style={{ ...inputStyle, flex: 2 }}
                                    />
                                    <input
                                      value={local.level}
                                      onChange={e => updateLocal(bat.id, local.id, 'level', e.target.value)}
                                      placeholder={t('project_manual_local_lvl')}
                                      style={{ ...inputStyle, flex: 1 }}
                                    />
                                    <button onClick={() => removeLocal(bat.id, local.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4, display: 'flex', alignItems: 'center' }}>
                                      <X size={14} />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => addLocal(bat.id)}
                                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--blue)', fontWeight: 500, alignSelf: 'flex-start' }}
                                >
                                  <FilePlus2 size={14} />{t('project_manual_add_local')}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        <button
                          onClick={addBatiment}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--blue)', fontWeight: 500, alignSelf: 'flex-start' }}
                        >
                          <FilePlus2 size={14} />{t('project_manual_add_bat')}
                        </button>
                        {batiments.length === 0 && (
                          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>{t('project_manual_no_bat_hint')}</p>
                        )}
                      </div>
                    )}

                    {/* Sous-onglet : Description du programme de travaux (saisie libre → LLM) */}
                    {subTab === 'programme' && (
                      <div>
                        <textarea
                          value={descriptionProgramme}
                          onChange={e => setDescriptionProgramme(e.target.value)}
                          rows={5}
                          style={{ ...inputStyle, resize: 'vertical' }}
                          placeholder={t('project_manual_programme_placeholder')}
                        />
                      </div>
                    )}

                    {/* Sous-onglet : Commentaires / précisions */}
                    {subTab === 'comments' && (
                      <div>
                        <textarea
                          value={constraints}
                          onChange={e => setConstraints(e.target.value)}
                          rows={3}
                          style={{ ...inputStyle, resize: 'vertical' }}
                          placeholder="…"
                        />
                      </div>
                    )}

                    {/* Sous-onglet : Exclusions */}
                    {subTab === 'exclusions' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {exclusions.length === 0 && (
                          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>{t('project_manual_exclusion_empty')}</p>
                        )}
                        {exclusions.map(exc => (
                          <div key={exc.id} style={{ border: '1px solid var(--gray)', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <label style={{ ...labelStyle, marginBottom: 0 }}>{t('project_manual_exclusion_type')}</label>
                              <button onClick={() => removeExclusion(exc.id)} title={t('project_manual_exclusion_remove')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4, display: 'flex', alignItems: 'center' }}>
                                <X size={14} />
                              </button>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              {['totale', 'partielle'].map(ty => (
                                <button
                                  key={ty}
                                  onClick={() => updateExclusion(exc.id, { type: ty, sous_type: ty === 'totale' ? null : exc.sous_type })}
                                  style={{
                                    padding: '6px 14px', borderRadius: 99, border: '1px solid',
                                    borderColor: exc.type === ty ? 'var(--blue)' : 'var(--gray)',
                                    background: exc.type === ty ? 'rgba(59,130,246,0.1)' : 'transparent',
                                    color: exc.type === ty ? 'var(--blue)' : 'var(--black)',
                                    fontWeight: exc.type === ty ? 600 : 400, fontSize: 13, cursor: 'pointer',
                                  }}
                                >
                                  {t(`project_manual_exclusion_${ty}`)}
                                </button>
                              ))}
                            </div>

                            <div>
                              <label style={labelStyle}>{t('project_manual_exclusion_locaux')}</label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={exc.tous_locaux}
                                  onChange={e => updateExclusion(exc.id, { tous_locaux: e.target.checked, locaux: [] })}
                                />
                                {t('project_manual_exclusion_all_locaux')}
                              </label>
                              {!exc.tous_locaux && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 220, overflowY: 'auto' }}>
                                  {allLocauxNames.length === 0 && (
                                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('project_manual_no_bat_hint')}</span>
                                  )}
                                  {batiments.filter(b => b.nom.trim() && b.locaux.some(l => l.name.trim())).map(bat => {
                                    const batLocalNames = bat.locaux.map(l => l.name).filter(Boolean);
                                    const allBatSelected = batLocalNames.length > 0 && batLocalNames.every(n => exc.locaux.includes(n));
                                    return (
                                      <div key={bat.id}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--black)', cursor: 'pointer', marginBottom: 4 }}>
                                          <input
                                            type="checkbox"
                                            checked={allBatSelected}
                                            onChange={e => updateExclusion(exc.id, {
                                              locaux: e.target.checked
                                                ? [...new Set([...exc.locaux, ...batLocalNames])]
                                                : exc.locaux.filter(n => !batLocalNames.includes(n)),
                                            })}
                                          />
                                          {bat.nom} — {t('project_manual_exclusion_all_locaux')}
                                        </label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginLeft: 22 }}>
                                          {batLocalNames.map(name => (
                                            <label key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                                              <input
                                                type="checkbox"
                                                checked={exc.locaux.includes(name)}
                                                onChange={e => updateExclusion(exc.id, {
                                                  locaux: e.target.checked ? [...exc.locaux, name] : exc.locaux.filter(n => n !== name),
                                                })}
                                              />
                                              {name}
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {exc.type === 'partielle' && (
                              <>
                                <div>
                                  <label style={labelStyle}>{t('project_manual_exclusion_soustype')}</label>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {['perimetre', 'programme', 'les_deux'].map(st => (
                                      <button
                                        key={st}
                                        onClick={() => updateExclusion(exc.id, { sous_type: st })}
                                        style={{
                                          padding: '6px 12px', borderRadius: 99, border: '1px solid',
                                          borderColor: exc.sous_type === st ? 'var(--blue)' : 'var(--gray)',
                                          background: exc.sous_type === st ? 'rgba(59,130,246,0.1)' : 'transparent',
                                          color: exc.sous_type === st ? 'var(--blue)' : 'var(--black)',
                                          fontWeight: exc.sous_type === st ? 600 : 400, fontSize: 12, cursor: 'pointer',
                                        }}
                                      >
                                        {t(`project_manual_exclusion_soustype_${st === 'les_deux' ? 'both' : st}`)}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                {(exc.sous_type === 'perimetre' || exc.sous_type === 'les_deux') && (
                                  <div>
                                    <label style={labelStyle}>{t('project_manual_exclusion_comment_perimetre')}</label>
                                    <textarea
                                      value={exc.commentaire_perimetre || ''}
                                      onChange={e => updateExclusion(exc.id, { commentaire_perimetre: e.target.value })}
                                      rows={2}
                                      style={{ ...inputStyle, resize: 'vertical' }}
                                    />
                                  </div>
                                )}
                                {(exc.sous_type === 'programme' || exc.sous_type === 'les_deux') && (
                                  <div>
                                    <label style={labelStyle}>{t('project_manual_exclusion_comment_programme')}</label>
                                    <textarea
                                      value={exc.commentaire_programme || ''}
                                      onChange={e => updateExclusion(exc.id, { commentaire_programme: e.target.value })}
                                      rows={2}
                                      style={{ ...inputStyle, resize: 'vertical' }}
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                        <button
                          onClick={addExclusion}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--blue)', fontWeight: 500, alignSelf: 'flex-start' }}
                        >
                          <FilePlus2 size={14} />{t('project_manual_exclusion_add')}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={handleSaveManual}
                  disabled={savingManual}
                  style={{ ...btnPrimary, alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, opacity: savingManual ? 0.6 : 1 }}
                >
                  <Save size={14} />{savingManual ? '⏳' : t('project_manual_save')}
                </button>
              </div>
            )}

            {/* ── Onglet Résumé saisie ─────────────────────────────────────── */}
            {activeTab === 'summary' && (
              <div style={{ maxWidth: 600 }}>
                {!latestManual ? (
                  <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
                    {t('project_summary_empty')}
                    <div style={{ marginTop: 12 }}>
                      <button onClick={() => setActiveTab('manual')} style={{ ...btnPrimary, fontSize: 12 }}>
                        {t('project_manual_tab')} →
                      </button>
                    </div>
                  </div>
                ) : (
                  <SummaryView doc={latestManual} isDAAT={isDAAT} compLabel={compLabel} t={t}
                    isManualStructuring={isManualStructuring}
                    onEdit={() => {
                      // Pré-remplir le formulaire avec la saisie existante avant de
                      // basculer sur l'onglet — sinon "Modifier" ouvre un formulaire
                      // vide et un Enregistrer accidentel écrase la saisie existante.
                      // normalizeManualStructuredData couvre aussi les anciennes
                      // saisies (pré-v2, locaux à plat sans bâtiment).
                      const sd = normalizeManualStructuredData(latestManual.structured_data) || {};
                      setConstraints(sd.descriptions_complementaires || '');
                      if (isDAAT) {
                        setDemoType(sd.demo_type || 'totale');
                      }
                      setBatiments(sd.batiments || []);
                      setDescriptionProgramme(sd.description_programme || '');
                      setExclusions(sd.exclusions || []);
                      setSubTab('locaux');
                      setActiveTab('manual');
                    }}
                    onDelete={() => setConfirmDoc({ projectId: selectedId, docId: latestManual.id })}
                  />
                )}
              </div>
            )}

            {/* ── Onglet Mes analyses ───────────────────────────────────────── */}
            {activeTab === 'analyses' && (
              <div style={{ width: '100%' }}>
                {loadingAnalyses ? (
                  <div style={{ padding: '20px 0', color: 'var(--text-muted)', fontSize: 13 }}>⏳</div>
                ) : analyses.length === 0 ? (
                  <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
                    <BarChart2 size={36} style={{ opacity: 0.2, marginBottom: 10 }} />
                    <div>{t('project_analyses_empty')}</div>
                    {isActive && (
                      <div style={{ marginTop: 12 }}>
                        {/* Même condition que le bouton de l'en-tête : deux
                            entrées visuelles, une seule règle. */}
                        <button
                          onClick={handleLaunchAnalysis}
                          disabled={isProcessing}
                          title={isProcessing ? t('project_launch_blocked') : undefined}
                          style={{ ...btnSuccess, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6,
                                   opacity: isProcessing ? 0.5 : 1,
                                   cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                        >
                          <PlayCircle size={13} />{t('project_launch_analysis')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {analyses.map(a => (
                      <AnalysisCard
                        key={a.id}
                        analysis={a}
                        isAdmin={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

          </>
        )}

        {/* Message erreur */}
        {error && (
          <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', fontSize: 13, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle size={14} />
            {error}
            <button onClick={() => setError('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 14 }}>✕</button>
          </div>
        )}
      </div>

      {/* Modales de confirmation */}
      {confirmProject && (
        <ConfirmModal
          message={t('project_delete_confirm')}
          onConfirm={handleDeleteProject}
          onCancel={() => setConfirmProject(null)}
        />
      )}
      {confirmDoc && (
        <ConfirmModal
          message={t('admin_delete_confirm')}
          onConfirm={handleDeleteDoc}
          onCancel={() => setConfirmDoc(null)}
        />
      )}
    </div>
  );
};

// ─── Résumé saisie manuelle ───────────────────────────────────────────────────

// Normalise une saisie manuelle enregistrée avant ce chantier (locaux à plat,
// pas de bâtiments/description_programme/exclusions) vers le schéma v2 —
// aucune saisie existante en DB n'est perdue ni cassée à l'affichage/édition.
const normalizeManualStructuredData = (sd) => {
  if (!sd) return null;
  if (sd.version === 2 && sd.batiments) return sd;
  const legacyLocaux = sd.locaux || [];
  return {
    ...sd,
    version: 2,
    batiments: legacyLocaux.length
      ? [{
          id: 'legacy',
          nom: '',
          locaux: legacyLocaux.map((l, i) => ({ id: `legacy-loc-${i}`, name: l.name, level: l.level })),
        }]
      : [],
    description_programme: sd.description_programme || '',
    exclusions: sd.exclusions || [],
  };
};

const SummaryView = ({ doc, isDAAT, compLabel, t, onEdit, onDelete, isManualStructuring }) => {
  const sdRaw = doc.structured_data;
  if (!sdRaw) return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</div>;
  const sd = normalizeManualStructuredData(sdRaw);

  const isDemo = sd.type === 'demolition';
  const savedAt = doc.created_at ? new Date(doc.created_at).toLocaleDateString('fr-FR') : '—';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2 size={16} style={{ color: '#6366f1' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--black)' }}>
            {isDemo ? t('project_mission_daat') : t('project_mission_raat')}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>— {savedAt}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onEdit} disabled={isManualStructuring} style={{ ...btnSecondarySmall, display: 'flex', alignItems: 'center', gap: 4, opacity: isManualStructuring ? 0.5 : 1, cursor: isManualStructuring ? 'not-allowed' : 'pointer' }}>
            <Edit3 size={12} /> Modifier
          </button>
          <button onClick={onDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 12 }}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {isManualStructuring && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.25)', fontSize: 13, color: '#1e40af' }}>
          <Clock size={13} style={{ flexShrink: 0 }} className="nms-spin-slow" />
          {t('project_manual_programme_llm_processing')}
        </div>
      )}

      {isDemo ? (
        <SummaryBlock label="Type de démolition" value={
          sd.demo_type === 'totale' ? t('project_manual_demo_totale') : t('project_manual_demo_partielle')
        } />
      ) : null}

      {/* Bâtiments et locaux (v2) — sinon fallback zones à plat (legacy sans nom de bâtiment) */}
      {sd.batiments && sd.batiments.length > 0 ? (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>{t('project_summary_batiments')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sd.batiments.map(bat => (
              <div key={bat.id} style={{ fontSize: 13, background: 'var(--bg-surface)', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--gray)' }}>
                {bat.nom && <strong>{bat.nom}</strong>}
                {bat.locaux && bat.locaux.length > 0 && (
                  <span style={{ color: 'var(--text-muted)' }}>
                    {bat.nom ? ' — ' : ''}{bat.locaux.map(l => l.name).filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : sd.zones && sd.zones.length > 0 && (
        <SummaryBlock label={t('project_manual_perimetre')} value={
          sd.zones.length === 1 && sd.zones[0] === 'Bâtiment entier'
            ? t('project_manual_batiment')
            : sd.zones.join(', ')
        } />
      )}

      {/* Description du programme de travaux (saisie libre) */}
      {sd.description_programme && (
        <SummaryBlock label={t('project_summary_programme_desc')} value={sd.description_programme} />
      )}

      {/* Composants — extraits par le LLM (v2) ou anciens ids checklist (legacy) */}
      {!isDemo && sd.composants && sd.composants.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
            {t('project_manual_composants')}
            {sd.programme_llm_status === 'done' && (
              <span style={{ marginLeft: 6, fontWeight: 400, fontSize: 11 }}>({t('project_manual_programme_llm_done')})</span>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {sd.composants.map((id, i) => {
              const comp = COMPOSANTS_A1.find(c => c.id === id);
              return (
                <span key={`${id}-${i}`} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 99, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', color: 'var(--blue)', fontWeight: 500 }}>
                  {comp ? compLabel(comp) : id}
                </span>
              );
            })}
          </div>
        </div>
      )}
      {sd.programme_llm_status === 'error' && (
        <div style={{ fontSize: 12, color: '#ef4444' }}>{t('project_manual_programme_llm_error')}</div>
      )}

      {/* Descriptions complémentaires */}
      {sd.descriptions_complementaires && (
        <SummaryBlock label={t('project_manual_constraints')} value={sd.descriptions_complementaires} />
      )}

      {/* Exclusions déclarées */}
      {sd.exclusions && sd.exclusions.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>{t('project_summary_exclusions')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sd.exclusions.map(exc => (
              <div key={exc.id} style={{ fontSize: 12, background: 'var(--bg-surface)', padding: '6px 10px', borderRadius: 7, border: '1px solid var(--gray)' }}>
                <strong>{exc.type === 'totale' ? t('project_summary_exclusion_totale_label') : t('project_summary_exclusion_partielle_label')}</strong>
                {' — '}
                {exc.tous_locaux ? t('project_manual_exclusion_all_locaux') : (exc.locaux || []).join(', ')}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryBlock = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 13, color: 'var(--black)', background: 'var(--bg-surface)', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--gray)' }}>
      {value}
    </div>
  </div>
);

// ─── Styles partagés ──────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 7,
  border: '1px solid var(--gray)', background: 'var(--bg-input)',
  fontSize: 13, color: 'var(--black)', outline: 'none', boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: 'var(--text-muted)', marginBottom: 6,
};
const btnPrimary = {
  padding: '8px 16px', borderRadius: 8, background: 'var(--blue)',
  color: 'white', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const btnSuccess = {
  padding: '8px 14px', borderRadius: 8, background: '#10b981',
  color: 'white', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const btnSecondary = {
  padding: '8px 14px', borderRadius: 8, background: 'transparent',
  border: '1px solid var(--gray)', color: 'var(--black)', fontSize: 13, cursor: 'pointer',
};
const btnSecondarySmall = {
  padding: '5px 10px', borderRadius: 6, background: 'transparent',
  border: '1px solid var(--gray)', color: 'var(--black)', fontSize: 12, cursor: 'pointer',
};
const btnDanger = {
  padding: '8px 14px', borderRadius: 8, background: 'transparent',
  border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444', fontSize: 13, cursor: 'pointer',
};
const btnValidate = {
  padding: '8px 14px', borderRadius: 8, background: '#6366f1',
  color: 'white', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

export default Projects;
